import React, { useEffect, useState } from 'react';
import { Alert, Button, useToast } from '@razorpay/blade/components';
import { useQuery } from 'react-query';
import { useFormWithProvider } from 'forms';
import { useHistory, Redirect } from 'react-router-dom';
import { routePaths } from 'components/Routes/data';
import { CustomFieldsForm } from 'components/EmployeeCustomFields/CustomFieldsForm';
import { CustomFieldValueMap } from 'components/EmployeeCustomFields/types';
import {
  decodeFieldValues,
  encodeFieldValuesForSubmit,
  filterToEditableValues,
  validateCustomFieldValues,
} from 'components/EmployeeCustomFields/validation';
import { validateEmployeeCustomFields } from 'api/gql-utils/employeeCustomFields/mutations/validateEmployeeCustomFields';
import { getEmployeeCustomFields } from 'api/gql-utils/employeeCustomFields/queries/getEmployeeCustomFields';
import { PayrollFieldViolation } from 'api/gql-utils/employeeCustomFields/types';
import { EMPLOYEE_CUSTOM_FIELD_VALUES } from 'components/EmployeeCustomFields/hooks/useEmployeeCustomFields';
import { useAdditionalDetailsStep } from './hooks/useAdditionalDetailsStep';
import { FormFields } from './types';
import {
  FormHeading,
  FormFieldsWrapper,
  StyledForm,
  FormFooterWrapper,
  BorderedBox,
} from 'components/EmployeeOnboarding/components/styles';
import { useSelector } from 'react-redux';
import loggedInUserSelectors from 'reducers/loggedInUser/selectors';
import { generateUserId } from 'utils/commons';

const VALIDATION_FAILURE_MESSAGE = 'Could not validate details. Please retry.';

export const AdditionalDetails: React.FC = () => {
  const history = useHistory();
  const toast = useToast();
  const {
    editableDefinitions,
    shouldShowAdditionalDetailsStep,
    isLoading,
    isError,
    refetch,
  } = useAdditionalDetailsStep();
  const { formData, setFieldValue } = useFormWithProvider<FormFields>();
  const [violations, setViolations] = useState<PayrollFieldViolation[]>([]);
  const [isValidating, setIsValidating] = useState(false);

  // CRITICAL: BFF asserts userId matches /^user_[A-Za-z0-9]+$/ on validate.
  // We use the logged-in employee's peopleId (same pattern as useSavedEmployeeData.ts)
  // and wrap it with generateUserId to produce the required "user_<id>" format.
  const loggedInPeopleId = useSelector(loggedInUserSelectors.peopleId);
  const userId = loggedInPeopleId ? generateUserId(String(loggedInPeopleId)) : null;

  // Pre-populate with the employee's OWN existing values. The self-service authz
  // lets an employee read their explicit id. We reuse the same values query key as
  // useEmployeeCustomFields so the cache is shared, and decode MONEY the same way.
  const existingValuesQuery = useQuery({
    queryKey: [EMPLOYEE_CUSTOM_FIELD_VALUES, userId],
    queryFn: () => getEmployeeCustomFields({ userId: userId as string }),
    enabled: Boolean(userId),
  });

  // Seed the form's customFields once, MERGING fetched values UNDER any values
  // the user has already entered (user edits always win). Runs when the fetch
  // resolves and definitions are known (decode needs the field types).
  const seededRef = React.useRef(false);
  useEffect(() => {
    if (seededRef.current) return;
    if (existingValuesQuery.isLoading || existingValuesQuery.data === undefined) return;
    if (editableDefinitions.length === 0) return;
    seededRef.current = true;
    const fetched = decodeFieldValues(
      editableDefinitions,
      (existingValuesQuery.data.payrollEmployeeCustomFields?.fields as Record<string, unknown>) ?? {},
    );
    // GetEmployeeFields also returns VIEW-only fields (e.g. admin-managed money
    // fields the employee cannot edit). Seed ONLY editable keys — anything else
    // in customFields would leak into the submit payload, which must stay
    // EDIT-only (HRIS rejects the whole write otherwise).
    const editableKeys = new Set(editableDefinitions.map((def) => def.fieldKey ?? ''));
    const fetchedEditable = Object.fromEntries(
      Object.entries(fetched).filter(([key]) => editableKeys.has(key)),
    );
    const entered = (formData.customFields as CustomFieldValueMap) ?? {};
    setFieldValue('customFields', { ...fetchedEditable, ...entered });
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [existingValuesQuery.isLoading, existingValuesQuery.data, editableDefinitions]);

  // Guard: if the flag is off or there are no editable fields (and loading is done),
  // skip this step and redirect directly to reviewDetails to prevent a dead-end.
  if (!isLoading && !shouldShowAdditionalDetailsStep) {
    return <Redirect to={routePaths.employeeOnboarding.employeeSelfOnboarding.reviewDetails} />;
  }

  const values: CustomFieldValueMap = (formData.customFields as CustomFieldValueMap) ?? {};

  const onChange = (fieldKey: string, value: string | null) =>
    setFieldValue('customFields', { ...values, [fieldKey]: value });

  const handleBack = () => {
    history.push(routePaths.employeeOnboarding.employeeSelfOnboarding.personalDetails);
  };

  const handleContinue = async () => {
    // Definitions failed to load — block Continue so mandatory custom fields are
    // never skipped. The retryable Alert (below) is the recovery path.
    if (isError) {
      return;
    }
    // Never send a bare "user_" prefix — the BFF rejects it. Bail out early if
    // the logged-in peopleId is unavailable.
    if (!userId) {
      toast.show({ content: VALIDATION_FAILURE_MESSAGE, color: 'negative' });
      return;
    }
    // Instant client-side required + type-format checks before the server call.
    const clientViolations = validateCustomFieldValues(editableDefinitions, values);
    if (clientViolations.length > 0) {
      setViolations(clientViolations);
      return;
    }
    setIsValidating(true);
    try {
      const result = await validateEmployeeCustomFields({
        userId,
        // MONEY values must be { amount_paise } objects on the wire.
        fields: encodeFieldValuesForSubmit(
          editableDefinitions,
          filterToEditableValues(editableDefinitions, values),
        ),
      });
      const all = result.payrollValidateEmployeeCustomFields.violations ?? [];
      setViolations(all);
      if (all.some((v) => v.severity === 'ERROR')) return;
      history.push(routePaths.employeeOnboarding.employeeSelfOnboarding.reviewDetails);
    } catch (e) {
      // Network/BFF failure — surface a retryable toast instead of failing silently.
      toast.show({ content: VALIDATION_FAILURE_MESSAGE, color: 'negative' });
    } finally {
      setIsValidating(false);
    }
  };

  return (
    <StyledForm>
      <FormFieldsWrapper>
        <FormHeading
          title="Additional Details"
          description="Please provide additional details to complete your profile."
        />
        <BorderedBox>
          {isError ? (
            <Alert
              color="negative"
              title="Couldn't load additional details"
              description="Please retry to continue."
              isDismissible={false}
              actions={{ primary: { text: 'Retry', onClick: () => refetch() } }}
            />
          ) : (
            <CustomFieldsForm
              definitions={editableDefinitions}
              values={values}
              onChange={onChange}
              violations={violations}
              showViewFieldsReadOnly={false}
            />
          )}
        </BorderedBox>
      </FormFieldsWrapper>

      <FormFooterWrapper>
        <Button variant="tertiary" size="medium" onClick={handleBack}>
          Back
        </Button>
        <Button
          variant="primary"
          size="medium"
          onClick={handleContinue}
          isDisabled={isError}
          isLoading={isValidating}>
          Continue
        </Button>
      </FormFooterWrapper>
    </StyledForm>
  );
};
